import {
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { approvalsRepositoryToken, defaultApprovalListQuery } from './repository';
import {
  financialApprovalActions,
  financialApprovalStatuses,
  type ApprovalListFilters,
  type ApprovalListQuery,
  type ApprovalSummary,
} from './types';

export function ApprovalListPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(approvalsRepositoryToken);
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [query, setQuery] = useState<ApprovalListQuery>(() => initialQuery(searchParams));
  const [history, setHistory] = useState<readonly string[]>(['']);
  const [page, setPage] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const resource = useResource(
    (signal) => repository.list(query, signal),
    [query, refresh, repository],
    (value) => value.items.length === 0,
  );
  const reset = (filters: ApprovalListFilters) => {
    setHistory(['']);
    setPage(0);
    setSearchParams({}, { replace: true });
    setQuery({ ...query, cursor: '', filters });
  };
  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('approvals.subtitle')}
        title={t('approvals.title')}
      />
      <ApprovalFilters onApply={reset} value={query.filters} />
      <PageState
        emptyMessage={t('approvals.empty')}
        error={toApprovalPageError(resource.error, t('approvals.errors.loadList'))}
        onRetry={() => setRefresh((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <>
            {mobile ? (
              <CompactApprovalResults items={resource.data.items} language={language} />
            ) : (
              <TableRegion label={t('approvals.table')}>
                <table aria-label={t('approvals.table')}>
                  <thead>
                    <tr>
                      <th>{t('approvals.fields.id')}</th>
                      <th>{t('approvals.fields.actionType')}</th>
                      <th>{t('approvals.fields.entityType')}</th>
                      <th>{t('approvals.fields.status')}</th>
                      <th>{t('approvals.fields.requestedBy')}</th>
                      <th>{t('approvals.fields.requestedAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resource.data.items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <Link to={`${paths.approvals}/${encodeURIComponent(item.id)}`}>
                            {item.id}
                          </Link>
                        </td>
                        <td>{item.actionType}</td>
                        <td>
                          {item.entityType} · {item.entityId ?? t('common.notAvailable')}
                        </td>
                        <td>{item.status}</td>
                        <td>{item.requestedBy}</td>
                        <td>
                          {formatTimestamp(item.requestedAt, 'Asia/Bangkok', language).display}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableRegion>
            )}
            {resource.data.requestId === null ? null : (
              <Typography variant="caption">
                {t('approvals.fields.requestId')}: {resource.data.requestId}
              </Typography>
            )}
            <CursorActions
              hasNext={resource.data.nextCursor !== null}
              page={page}
              onNext={() => {
                const cursor = resource.data?.nextCursor;
                if (cursor === null || cursor === undefined) return;
                const index = page + 1;
                setHistory((current) => [...current.slice(0, index), cursor]);
                setPage(index);
                setQuery((current) => ({ ...current, cursor }));
              }}
              onPrevious={() => {
                const index = page - 1;
                setPage(index);
                setQuery((current) => ({ ...current, cursor: history[index] ?? '' }));
              }}
            />
          </>
        )}
      </PageState>
    </Stack>
  );
}

function ApprovalFilters({
  onApply,
  value,
}: {
  readonly onApply: (filters: ApprovalListFilters) => void;
  readonly value: ApprovalListFilters;
}) {
  const initialValue = useRef(value);
  const { register, handleSubmit, reset } = useForm<ApprovalListFilters>({
    defaultValues: initialValue.current,
  });
  const { t } = useTranslation();
  return (
    <ListFilterPanel
      ariaLabel={t('filterPanel.label', { page: t('approvals.title') })}
      onClear={() => {
        reset(defaultApprovalListQuery.filters);
        onApply(defaultApprovalListQuery.filters);
      }}
      onSubmit={handleSubmit(onApply)}
      primaryFields={
        <>
          <TextField
            label={t('approvals.fields.partnerId')}
            size="small"
            {...register('partnerId')}
          />
          <TextField
            defaultValue={initialValue.current.actionType}
            label={t('approvals.fields.actionType')}
            select
            size="small"
            {...register('actionType')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {financialApprovalActions.map((action) => (
              <MenuItem key={action} value={action}>
                {action}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            defaultValue={initialValue.current.status}
            label={t('approvals.fields.status')}
            select
            size="small"
            {...register('status')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {financialApprovalStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('approvals.fields.entityType')}
            size="small"
            {...register('entityType')}
          />
          <TextField
            label={t('approvals.fields.entityId')}
            size="small"
            {...register('entityId')}
          />
        </>
      }
    />
  );
}

function CompactApprovalResults({
  items,
  language,
}: {
  readonly items: readonly ApprovalSummary[];
  readonly language: TimeLanguage;
}) {
  const { t } = useTranslation();
  return (
    <Stack
      aria-label={t('approvals.compact')}
      component="ul"
      spacing={1}
      sx={{ listStyle: 'none', m: 0, p: 0 }}
    >
      {items.map((item) => (
        <Paper component="li" key={item.id} sx={{ p: 2 }} variant="outlined">
          <Stack spacing={0.75}>
            <Link to={`${paths.approvals}/${encodeURIComponent(item.id)}`}>{item.id}</Link>
            <Typography variant="body2">{item.actionType}</Typography>
            <Typography variant="body2">
              {item.entityType} · {item.entityId ?? t('common.notAvailable')}
            </Typography>
            <Typography variant="body2">{item.status}</Typography>
            <Typography color="text.secondary" variant="caption">
              {formatTimestamp(item.requestedAt, 'Asia/Bangkok', language).display}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function TableRegion({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label: string;
}) {
  return (
    <Stack
      aria-label={label}
      role="region"
      tabIndex={0}
      sx={{
        overflowX: 'auto',
        '& table': { borderCollapse: 'collapse', minWidth: 900, width: '100%' },
        '& th, & td': { borderBottom: 1, borderColor: 'divider', p: 1, textAlign: 'left' },
      }}
    >
      {children}
    </Stack>
  );
}

function CursorActions({
  hasNext,
  onNext,
  onPrevious,
  page,
}: {
  readonly hasNext: boolean;
  readonly onNext: () => void;
  readonly onPrevious: () => void;
  readonly page: number;
}) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
      <Button disabled={page === 0} onClick={onPrevious}>
        {t('approvals.pagination.previous')}
      </Button>
      <Button disabled={!hasNext} onClick={onNext}>
        {t('approvals.pagination.next')}
      </Button>
    </Stack>
  );
}

function initialQuery(params: URLSearchParams): ApprovalListQuery {
  return {
    ...defaultApprovalListQuery,
    filters: {
      partnerId: (params.get('partnerId') ?? '').trim(),
      actionType: allow(params.get('actionType'), financialApprovalActions),
      status: allow(params.get('status'), financialApprovalStatuses),
      entityType: (params.get('entityType') ?? '').trim(),
      entityId: (params.get('entityId') ?? '').trim(),
    },
  };
}

function allow<const Value extends string>(
  value: string | null,
  values: readonly Value[],
): Value | '' {
  return value !== null && values.includes(value as Value) ? (value as Value) : '';
}

export function toApprovalPageError(
  error: unknown,
  fallback: string,
  knownMessages: Readonly<Record<string, string>> = {},
): Pick<AppError, 'message' | 'requestId'> | null {
  if (error === null || error === undefined) return null;
  const candidate = typeof error === 'object' && error !== null ? (error as Partial<AppError>) : {};
  return {
    message:
      typeof candidate.code === 'string' ? (knownMessages[candidate.code] ?? fallback) : fallback,
    requestId: typeof candidate.requestId === 'string' ? candidate.requestId : null,
  };
}
